import { createContext, useContext, useReducer } from 'react';

import { noop } from 'lodash';

import { CampaignStatus, TimeUnitEnum } from '../../../campaigns/marketing-campaign-builder/builder-context';
import { EVENT_TYPE_IDS } from '../../../campaigns/marketing-campaign-builder/helpers';
import { type WorkflowCampaignEdge, type WorkflowCampaignNode } from '../slides/workflow-editor/models';

import { computePositions, getReactFlowEdgesFromWorkflowEdges, getReactFlowNodesFromWorkflowNodes } from './helpers';

import type {
  WorkflowEdge,
  WorkflowMarketingCampaign,
  WorkflowNode,
} from 'src/app/queries/marketing/get-workflow-campaign';

// Basic node types
export type NodeType = 'action' | 'conditional' | 'timeDelay' | 'trigger';

// identical to the WorkflowMarketingCampaign type
export type WorkflowBuilderState = {
  campaignName: string;
  campaignStatusId: number;
  delayInMinutes: number | null;
  delayUnitId: number | null;
  edges: WorkflowCampaignEdge[];
  eventTypeId: number;
  isReadOnly: boolean;
  lastModifiedDateUTC: string | null;
  lastRuntimeDateUtc: string | null;
  lspId: number;
  marketingCampaignId: number | null;
  nodes: WorkflowCampaignNode[];
  recipientSegmentIds: number[];
  scheduleSendForUtc: string | null;
  selectedNode: WorkflowCampaignNode | null;
  selectedNodeId: string | null;
  selectedNodeIsNew: boolean;
  sendToAllCustomers: boolean;
  tagIds: number[];
  timeOfDayToSend: string | null;
  utmTerm: string | null;
};

export type WorkflowTemplate = {
  DelayInMinutes: number | null;
  DelayUnitId: number | null;
  Edges: WorkflowEdge[];
  EventTypeId: number;
  Nodes: WorkflowNode[];
  SendToAllCustomers: boolean;
  TimeOfDayToSend: string | null;
};

// Action types
type UpdateFullCampaignAction = {
  payload: WorkflowMarketingCampaign;
  type: 'update-full-campaign';
};

export type SetValueAction<K extends keyof WorkflowBuilderState> = {
  payload: { key: K; value: WorkflowBuilderState[K] };
  type: 'set-value';
};

type AddNodeAction = {
  payload: WorkflowCampaignNode;
  type: 'add-node';
};

export type SetNodeAction = {
  payload: {
    key: string;
    nodeId: string;
    value: any;
  };
  type: 'set-node';
};

export type SetNodeDataAction = {
  payload: {
    nodeId: string;
    value: any;
  };
  type: 'set-node-data';
};

type SetNodesAction = {
  payload: WorkflowCampaignNode[];
  type: 'set-nodes';
};

type SetSelectedNodeAction = {
  payload: {
    id: string;
    isNewNode: boolean;
  };
  type: 'set-selected-node';
};

type AddEdgeAction = {
  payload: WorkflowCampaignEdge;
  type: 'add-edge';
};

type SetEdgesAction = {
  payload: WorkflowCampaignEdge[];
  type: 'set-edges';
};

type ApplyTemplateAction = {
  payload: WorkflowTemplate;
  type: 'apply-template';
};

type ResetAction = {
  type: 'reset';
};

type ClearTemplateAction = {
  type: 'clear-template';
};

export type WorkflowAction =
  | AddEdgeAction
  | AddNodeAction
  | ApplyTemplateAction
  | ClearTemplateAction
  | ResetAction
  | SetEdgesAction
  | SetNodeAction
  | SetNodeDataAction
  | SetNodesAction
  | SetSelectedNodeAction
  | SetValueAction<keyof WorkflowBuilderState>
  | UpdateFullCampaignAction;

const defaultTemplate: WorkflowTemplate = {
  DelayInMinutes: 0,
  DelayUnitId: TimeUnitEnum.days,
  Edges: [],
  EventTypeId: EVENT_TYPE_IDS.POST_PURCHASE,
  Nodes: [],
  SendToAllCustomers: true,
  TimeOfDayToSend: null,
};

// Default state
const defaultBuilderState: WorkflowBuilderState = {
  campaignName: '',
  campaignStatusId: CampaignStatus.DRAFT,
  delayInMinutes: 0,
  delayUnitId: null,
  edges: [],
  eventTypeId: EVENT_TYPE_IDS.POST_PURCHASE,
  lastModifiedDateUTC: '',
  lastRuntimeDateUtc: '',
  lspId: 0,
  marketingCampaignId: null,
  nodes: [],
  recipientSegmentIds: [],
  scheduleSendForUtc: '',
  selectedNodeId: null,
  selectedNode: null, // derived from selectedNodeId
  selectedNodeIsNew: false, // derived from selectedNodeId
  sendToAllCustomers: true,
  timeOfDayToSend: '',
  utmTerm: null,
  isReadOnly: false,
  tagIds: [],
};

// Reducer
export function workflowBuilderReducer(state: WorkflowBuilderState, action: WorkflowAction): WorkflowBuilderState {
  switch (action.type) {
    case 'set-value':
      return {
        ...state,
        [action.payload.key]: action.payload.value,
      };

    case 'add-node':
      return {
        ...state,
        nodes: computePositions([...state.nodes, action.payload], state.edges, state.isReadOnly),
      };

    case 'add-edge':
      return {
        ...state,
        edges: [...state.edges, action.payload],
        nodes: computePositions(state.nodes, [...state.edges, action.payload], state.isReadOnly),
      };

    case 'reset':
      return defaultBuilderState;

    case 'set-nodes':
      return {
        ...state,
        nodes: computePositions(action.payload, state.edges, state.isReadOnly),
        selectedNode: state.nodes.find((node) => node.id === state.selectedNodeId) ?? null,
      };

    case 'set-edges':
      return {
        ...state,
        edges: action.payload,
        nodes: computePositions(state.nodes, action.payload, state.isReadOnly),
      };

    case 'set-selected-node': {
      return {
        ...state,
        selectedNodeId: action.payload.id,
        selectedNode: state.nodes.find((node) => node.id === action.payload.id) ?? null,
        selectedNodeIsNew: action.payload.isNewNode,
      };
    }

    case 'set-node': {
      const updatedNodes = state.nodes.map((node) =>
        node.id === action.payload.nodeId ? { ...node, [action.payload.key]: action.payload.value } : node
      );

      return {
        ...state,
        nodes: updatedNodes,
        selectedNode: updatedNodes.find((node) => node.id === state.selectedNodeId) ?? null,
      };
    }

    // update selectednode as well as the same node in the nodes array
    case 'set-node-data': {
      const updatedNodes = state.nodes.map((node) =>
        node.id === action.payload.nodeId ? { ...node, data: { ...node.data, ...action.payload.value } } : node
      );

      return {
        ...state,
        nodes: updatedNodes,
        selectedNode: updatedNodes.find((node) => node.id === state.selectedNodeId) ?? null,
      };
    }

    case 'update-full-campaign':
      return {
        ...state,
        ...getUpdatedStateFromWorkflowCampaign(state, action.payload),
      };

    case 'apply-template':
      return {
        ...state,
        ...getUpdatedStateFromTemplate(action.payload),
      };

    case 'clear-template':
      return {
        ...state,
        ...getUpdatedStateFromTemplate(defaultTemplate),
      };

    default:
      return state;
  }
}

export function getUpdatedStateFromTemplate(template: WorkflowTemplate): Partial<WorkflowBuilderState> {
  const edges = getReactFlowEdgesFromWorkflowEdges(template.Edges, template.Nodes);
  return {
    edges,
    nodes: computePositions(getReactFlowNodesFromWorkflowNodes(template.Nodes), edges, false),
    sendToAllCustomers: template.SendToAllCustomers,
    eventTypeId: template.EventTypeId,
    delayInMinutes: template.DelayInMinutes,
    delayUnitId: template.DelayUnitId,
    timeOfDayToSend: template.TimeOfDayToSend,
    selectedNodeId: null,
    selectedNode: null,
    selectedNodeIsNew: false,
  };
}

export function getUpdatedStateFromWorkflowCampaign(
  state: WorkflowBuilderState,
  campaign: WorkflowMarketingCampaign
): Partial<WorkflowBuilderState> {
  const edges = getReactFlowEdgesFromWorkflowEdges(campaign.Edges, campaign.Nodes);
  const nodes = computePositions(getReactFlowNodesFromWorkflowNodes(campaign.Nodes), edges, state.isReadOnly);
  return {
    campaignName: campaign.CampaignName,
    campaignStatusId: campaign.CampaignStatusId,
    delayInMinutes: campaign.DelayInMinutes,
    delayUnitId: campaign.DelayUnitId,
    edges,
    eventTypeId: campaign.EventTypeId,
    lastModifiedDateUTC: campaign.LastModifiedDateUTC,
    lastRuntimeDateUtc: campaign.LastRuntimeDateUtc,
    lspId: campaign.LspId,
    marketingCampaignId: campaign.MarketingCampaignId,
    nodes,
    recipientSegmentIds: campaign.RecipientSegmentIds,
    scheduleSendForUtc: campaign.ScheduleSendForUtc,
    sendToAllCustomers: campaign.SendToAllCustomers,
    timeOfDayToSend: campaign.TimeOfDayToSend,
    selectedNode: nodes.find((node) => node.id === state.selectedNodeId) ?? null,
    utmTerm: campaign.CustomUtmParameters?.find((param) => param.ParameterName === 'utm_term')?.ParameterValue ?? null,
    tagIds: campaign.TagIds ?? [],
  };
}

// Context and hooks
export function useWorkflowBuilderReducer() {
  return useReducer(workflowBuilderReducer, defaultBuilderState);
}

export function useReadonlyWorkflowBuilderReducer() {
  return useReducer(workflowBuilderReducer, { ...defaultBuilderState, isReadOnly: true });
}

export const WorkflowBuilderContext = createContext<ReturnType<typeof useWorkflowBuilderReducer>>([
  defaultBuilderState,
  noop,
]);

export function useWorkflowBuilderContext() {
  return useContext(WorkflowBuilderContext);
}
